import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import { ITradeDealService, CHANNEL_TRADE_SERVICE } from "../ITradeDealService";
import { ENCODABLE_TYPE_ERRORS } from "shared/broker-client-services";
import { TradeDealAdapter } from "../adapter/TradeDealAdapter";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { TraderGroupLookupService } from "../../lookup/model/impl/TraderGroupLookupService";
import {
  AlgoClientTradeLookupService,
  BMClientTradeLookupService,
  LimitClientTradeLookupService,
  TradeDealLookupService,
} from "../../lookup/model/impl/TradeDealLookupService";
import { TradeDealPromiseActionService } from "../../action/impl/TradeDealPromiseActionService";
import { TradeDeal } from "../../model/core/TradeDeal";
import { TradeRate } from "../../model/core/TradeRate";
import { DealPrivatizationService } from "../../model/privatization/DealPrivatizationService";

import { Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import { BaseLookupService } from "../../lookup/impl/BaseLookupService";
import {
  LimitRiskTradeLookupService,
  AlgoRiskTradeLookupService,
  BMRiskTradeLookupService,
} from "../../lookup/model/impl/RiskTradeLookupService";
const logger = LoggerService.for(__dirname);

@Injectable()
export class TradeDealPrimaryService implements ITradeDealService {
  protected broker: ServiceMessageBroker;
  protected port: MessagePort = null;
  protected lifeCycleEventSubject: Subject<CubiqTS.DealImpl> = new Subject<CubiqTS.DealImpl>();

  protected services: BaseLookupService<string, CubiqTS.DealImpl>[];

  constructor(
    private worker: CubiqWorker,
    protected factory: ServiceMessageBrokerFactory,
    private tradeDealAdapter: TradeDealAdapter,
    private tradeDealPromiseActionService: TradeDealPromiseActionService,
    private limitClientLookupService: LimitClientTradeLookupService,
    private algoClientLookupService: AlgoClientTradeLookupService,
    private bmClientLookupService: BMClientTradeLookupService,
    private limitRiskLookupService: LimitRiskTradeLookupService,
    private algoRiskLookupService: AlgoRiskTradeLookupService,
    private bmRiskLookupService: BMRiskTradeLookupService,
    private dealPrivatizationService: DealPrivatizationService,
  ) {
    this.broker = this.factory.createMessageBroker(CHANNEL_TRADE_SERVICE, true, true);
    this.broker.registerMethod(
      "confirmTradeDeal",
      [PRIMITIVE],
      this.confirmTradeDeal.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "confirmTradeDeals",
      [PRIMITIVE],
      this.confirmTradeDeals.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "cancelTradeDeal",
      [PRIMITIVE],
      this.cancelTradeDeal.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "cancelTradeDeals",
      [PRIMITIVE],
      this.cancelTradeDeals.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "addFillComment",
      [PRIMITIVE, PRIMITIVE],
      this.addFillComment.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getTradeDeals",
      [PRIMITIVE],
      this.getTradeDeals.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("initPort", [PRIMITIVE], this.initPort.bind(this), null, false);
    this.broker.registerMethod(
      "getTradeDealDetails",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.getTradeDealDetails.bind(this),
      PRIMITIVE,
      false,
    );
    this.services = [
      this.limitClientLookupService,
      this.algoClientLookupService,
      this.bmClientLookupService,
      this.limitRiskLookupService,
      this.algoRiskLookupService,
      this.bmRiskLookupService,
    ];
  }

  public initPort(port: MessagePort): void {
    this.port = port;
  }

  /// ACTIONS
  public confirmTradeDeal(deal: TradeDeal): Promise<any> {
    let response: any = this.tradeDealPromiseActionService.confirmTrade(
      deal.dealId,
      CubiqTS.TradeActionType.CONFIRM,
    );
    return this.processTradeResponse(response);
    /*return new Promise<boolean>((resolve, reject) => {
            this.tradeDealPromiseActionService.confirmTrade(deal.dealId, CubiqTS.TradeActionType.CONFIRM).then((payload: any) => {
               let result: any = payload;
               if (payload === null || payload === undefined){
                   result = true;
               }
               resolve(result);
            }, (error: any) => {
                reject(error);
            });
        });
*/
    // return this.processTradeResponse(response);
  }

  public confirmTradeDeals(deals: Array<TradeDeal>): Promise<any> {
    let dealIds = new Array<string>();

    deals.forEach((deal) => {
      dealIds.push(deal.dealId);
    });

    let response: any = this.tradeDealPromiseActionService.confirmTrades(
      dealIds,
      CubiqTS.TradeActionType.CONFIRM,
    );
    return this.processTradeResponse(response);
  }

  public cancelTradeDeal(deal: TradeDeal): Promise<any> {
    let response: any = this.tradeDealPromiseActionService.cancelTrade(
      deal.dealId,
      CubiqTS.TradeActionType.CANCEL,
    );
    return this.processTradeResponse(response);
  }

  public cancelTradeDeals(deals: Array<TradeDeal>): Promise<any> {
    let dealIds = new Array<string>();

    deals.forEach((deal) => {
      dealIds.push(deal.dealId);
    });

    let response: any = this.tradeDealPromiseActionService.cancelTrades(
      dealIds,
      CubiqTS.TradeActionType.CANCEL,
    );
    return this.processTradeResponse(response);
  }

  public addFillComment(deal: TradeDeal, comment: string): Promise<any> {
    let response: any = this.tradeDealPromiseActionService.addFillComment(
      deal.dealId,
      CubiqTS.TradeActionType.COMMENT,
      comment,
    );
    return this.processTradeResponse(response);
  }

  public getTradeDeals(dealIds: Array<string>): Promise<Array<TradeDeal>> {
    return new Promise<Array<TradeDeal>>((res) => {
      let results: Array<TradeDeal> = Array.isArray(dealIds)
        ? dealIds.reduce((deals, id) => {
            this.services.some((service) => {
              const deal = service.getItem(id);
              if (deal != null) {
                deals.push(this.tradeDealAdapter.transformDeal(deal));
                return true;
              }
              return false;
            });

            return deals;
          }, [])
        : [];

      res(results);
    });
  }

  public adaptTradeDeals(deals: Array<CubiqTS.DealImpl>): Promise<Array<TradeDeal>> {
    return Promise.resolve(deals.map((deal) => this.tradeDealAdapter.transformDeal(deal)));
  }

  /// HELPERS
  protected processTradeResponse(response: Promise<any>): Promise<any> {
    return new Promise<any>((res, rej) => {
      response.then((value: any) => {
        let response: any;

        if (value && value.enType && value.enType == ENCODABLE_TYPE_ERRORS) {
          response = value.errorMessages[0];
        } else {
          response = true;
        }
        res(response);
      });
    });
  }

  public getTradeDealDetails(
    dealId: string,
    includeDetails: boolean,
    includeConfirmations: boolean,
  ): Promise<TradeDeal> {
    return new Promise<TradeDeal>((res, rej) => {
      let tradeStatusRequest = new CubiqTS.TradeStatusRequestImpl();
      tradeStatusRequest.dealIds = [new CubiqTS.Long(Number(dealId))];
      tradeStatusRequest.orderTypes = null;

      tradeStatusRequest.includeComponentDetails = includeDetails;
      tradeStatusRequest.includeDealConfirmations = includeConfirmations;
      tradeStatusRequest.includeCounterPartyInformation = true;

      this.worker.postServiceRequest(tradeStatusRequest).then(
        (value: CubiqTS.StandardReportImpl) => {
          let tradeDeals: Array<CubiqTS.DealImpl> = Array.isArray(value.results)
            ? value.results.map((d) => this.dealPrivatizationService.checkPrivatization(d))
            : [];

          console.debug("Trade deal details:", tradeDeals);

          if (tradeDeals && tradeDeals.length == 1) {
            let deal = this.tradeDealAdapter.transformDeal(tradeDeals[0]);
            res(deal);
          } else {
            rej("More than 1 deal returned");
          }
        },
        (reason) => {
          rej(reason);
        },
      );
    });
  }
}
